import React, { useMemo } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { DateText, StyledTextBox, CoupleWrapper, NoteAndPhotoRow, DashboardButtonWrapper, styles, StyledRoot, StyledTitle } from './CoupleHeader.styles';
import { useCoupleHeaderController } from './CoupleHeader.controller';
import { PageNoteText } from './PageNoteText';
import { DashboardButton, NavBar } from '@apps/registry/guest/components';
import { WebsiteV2BackToSiteLink } from '@shared/components/WebsiteV2BackToSiteLink';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import type { DesignLayoutType } from '@graphql/generated-base';
import { Category } from '@graphql/generated-base';
import type { UserProfile } from '@shared/components/AuthProvider';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventType } from '@shared/utils/eventType';
import { useRegistryGuestTranslations } from '../../GuestRegistry.i18n';
import { EventPhoto } from '@apps/registry/common/components/EventPreview/components/EventPhoto/EventPhoto';
import { getEventTypeTranslationWithGeneralFallback } from '@shared/utils/getEventTypeTranslationWithGeneralFallback';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { display3ToPageTitleOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { isInIframe } from '@shared/utils/isInIframe';
import type { OverrideTypography } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.types';
import { brannanConfig } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/LayoutBrannan.theme';
import { useWebsitePages } from '@shared/hooks/useWebsitePages';
import { toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { CustomArtMobile } from '@apps/guest/packages/layout-engine/components/CustomArtMobile/CustomArtMobile';
import { getCustomArtBanner } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { addRendition } from '@shared/utils/photoRendition';
import { useEventDesignThemingProvider } from '@shared/core/website/theming';
import { EVENT_TITLE_TYPOGRAPHY_OVERRIDE, getEventNameMinHeight } from '@apps/guest/components/EventNameBanner/eventNameBanner.constants';
import { useEventNameTitleVariant } from '@apps/guest/components/EventNameBanner/useEventNameTitleVariant';
import type { EventDesignFragment } from '@graphql/fragments/eventDesign.graphql.generated';

const { componentTitleBanner } = brannanConfig.applicatorHtmlProps;

// Mobile custom art for the registry title banner. The desktop body art (Shell's fixed
// CustomArt) is only visible in the centered card's side margins, which the full-width mobile
// card doesn't have — so on mobile the art must render as an absolutely-positioned image in
// the title banner (mirroring the website header's EventNameNav). Extracted for unit-testability.
// Returns null when the art should not render (desktop, custom art disabled, or no banner).
export const getCoupleHeaderMobileCustomArtUrl = (eventDesign: Maybe<EventDesignFragment>, isMobile: boolean, isNewCustomArtPanelEnabled = false): string | null => {
  const banner = getCustomArtBanner(eventDesign ?? undefined, isNewCustomArtPanelEnabled);
  return isMobile && banner.isEnabled ? addRendition({ url: banner.url, renditionSize: 'medium' }) : null;
};

interface BrannanHeaderProps {
  website: string;
  font: string;
  emptyNote: boolean;
  layout?: DesignLayoutType;
  primaryTextColor?: string;
  pages?: ReadonlyArray<EventPageFragment>;
  currentUser: UserProfile;
  onHandleLogout: () => void;
  isAdmin: boolean;
  isNavBarVisible: boolean;
  isBackToSiteVisible: boolean;
  // Mobile-only custom art. The desktop body art comes from the Shell's fixed CustomArt
  // (visible in the centered card's side margins); on mobile the card is full-width with
  // no margins, so the art must render as an absolutely-positioned image inside the title
  // banner — mirroring the website header's EventNameNav. Null when it should not render.
  mobileCustomArtUrl?: string | null;
}

interface Props
  extends Readonly<{
    website: string;
    eventDate?: string | null;
    pageNote?: string | null;
    isAdmin: boolean;
    registryPhoto?: string;
    eventId: string;
    firebaseId?: string;
    font?: string;
    pages?: ReadonlyArray<EventPageFragment>;
    layout?: DesignLayoutType;
    primaryTextColor?: string;
    isNative: boolean;
    // PRNT-4193 — when set, admin photo edits route through the migrated
    // `uploadPhotoFromUrl` → `updatePageContainer` flow instead of legacy
    // `setEventPageAsset` / `removeEventPagePhoto`. See `EventPhoto.tsx`.
    migratedPageContainerId?: string;
  }> {}

const BrannanHeader = ({
  website,
  font,
  emptyNote,
  layout,
  pages,
  currentUser,
  onHandleLogout,
  primaryTextColor,
  isAdmin,
  isNavBarVisible,
  isBackToSiteVisible,
  mobileCustomArtUrl,
}: BrannanHeaderProps) => {
  const { eventType } = useEventType();
  const { getCoupleHeaderTranslations } = useRegistryGuestTranslations();
  const { eventInfo } = useEventInfo();
  const translations = getCoupleHeaderTranslations().defaultName;
  const { artBackgroundFontColor } = useColorPaletteProvider();

  const defaultDisplayName = getEventTypeTranslationWithGeneralFallback(translations, eventType)();

  const isPreviewing = isInIframe();
  const eventNameMinHeight = useMemo(() => getEventNameMinHeight(isPreviewing, false), [isPreviewing]);
  const eventNameTitleVariant = useEventNameTitleVariant();

  return (
    <>
      {/* Below sm4 (960) this element is painted with artBackgroundColor by the style applicator,
          so its padding and the title's marginBottom show as bare color — zeroed to match EventNameNav. */}
      <StyledRoot
        position={'relative'}
        minHeight={eventNameMinHeight}
        paddingTop={!isAdmin ? { _: 0, sm4: 8 } : 0}
        {...componentTitleBanner.prop}
      >
        {mobileCustomArtUrl && <CustomArtMobile backgroundImageUrl={mobileCustomArtUrl} />}
        <StyledTitle
          __css={styles.title}
          color={{ _: artBackgroundFontColor, sm4: 'inherit' }}
        >
          <GuestSiteTypographyOverride
            override={EVENT_TITLE_TYPOGRAPHY_OVERRIDE}
            defaultFontFamily={font}
          >
            <TextV2
              typographyVariant={eventNameTitleVariant}
              fontWeight={400}
              marginBottom={{ _: 0, sm4: emptyNote ? 9 : 56 }}
            >
              {eventInfo?.eventDisplayName ?? defaultDisplayName}
            </TextV2>
          </GuestSiteTypographyOverride>
        </StyledTitle>
      </StyledRoot>
      <Flex
        justifyContent={'space-between'}
        alignItems={'center'}
        paddingX={['10px', null, '0px']}
        width={'100%'}
      >
        {isNavBarVisible && (
          <NavBar
            layout={layout}
            pages={pages}
            font={font}
            eventHandle={website}
            currentUser={currentUser}
            onHandleLogout={onHandleLogout}
            primaryTextColor={primaryTextColor}
          />
        )}
      </Flex>
      {/* Centered under the event name, where a Brannan site's own nav sits, pulled up into the
          title's bottom margin the same way the V1 NavBar is. */}
      {isBackToSiteVisible && (
        <Flex
          justifyContent="center"
          marginTop={{ _: 5, sm4: -7 }}
          marginBottom={{ _: 0, sm4: 6 }}
        >
          <WebsiteV2BackToSiteLink
            eventHandle={website}
            color={primaryTextColor}
          />
        </Flex>
      )}

      <Flex margin="0 auto">
        {/* Manage Registry Button */}
        {isAdmin && (
          <DashboardButtonWrapper __css={styles.dashboardButtonWrapperBrannan}>
            <DashboardButton website={website} />
          </DashboardButtonWrapper>
        )}
      </Flex>
    </>
  );
};

export const CoupleHeader: React.FC<Props> = ({
  eventDate,
  pageNote,
  isAdmin,
  registryPhoto,
  website,
  firebaseId,
  eventId,
  font = 'Austin News Headline',
  pages,
  layout,
  primaryTextColor,
  isNative,
  migratedPageContainerId,
}) => {
  const { handleOnUpdatePhoto, handleDeletePhoto, onEditPageNote, onContinueReading, currentUser, onHandleLogout, isNavBarVisible, isBackToSiteVisible, photo } =
    useCoupleHeaderController({
      eventId,
      firebaseId,
      isAdmin,
      website,
      registryPhoto,
      pages,
      layout,
    });
  const isMobile = useMediaQuery(theme => theme.mediaQueries.down({ breakpointAlias: 'sm4' }));

  const dateTypographyOverride: OverrideTypography = useMemo(() => [{ variant: isMobile ? 'hed3' : 'hed5', category: Category.DATE }], [isMobile]);

  const { getPageByName } = useWebsitePages();
  const registryPage = getPageByName('registrylinks');
  const eventPageId = registryPage?.id;

  // getPageByName keys by exact pageSlug; migrated registrylinks events store the registry
  // page as pageSlug 'registry' (canonical 'registrylinks'), so resolve the title canonically.
  const registryPageTitle = pages?.find(page => toCanonicalMigratedSlug(page.pageSlug) === 'registrylinks')?.pageTitle;
  const [showStandalonePageTitle] = useIsFeatureEnabled('registryShowStandalonePageTitle');

  const photoExists = !!photo;
  const emptyNote = !pageNote?.length;

  // Read the design from the theming provider (not registryState) so live preview edits to
  // mobile art in the Website Designer update without an iframe reload.
  const { eventDesign } = useEventDesignThemingProvider();
  // Held while the flag resolves, so a flagged event doesn't paint the legacy banner and drop it a tick later.
  const [isNewCustomArtPanelEnabled, isNewCustomArtFlagLoading] = useIsFeatureEnabled('newCustomArtPanelEnabled');
  const mobileCustomArtUrl = isNewCustomArtFlagLoading ? null : getCoupleHeaderMobileCustomArtUrl(eventDesign, isMobile, isNewCustomArtPanelEnabled);

  // Below sm4 the event-name banner is painted with artBackgroundColor, so the first block after
  // it needs a gap or it butts against the band. Applied to the page title when it resolves,
  // otherwise to the content wrapper.
  const showPageTitle = !isNative && showStandalonePageTitle && !!registryPageTitle;
  const bannerContentTopPadding = isNative ? undefined : { _: 6, sm4: 0 };

  return (
    <Box>
      {!isNative && (
        <BrannanHeader
          layout={layout}
          pages={pages}
          font={font}
          currentUser={currentUser}
          onHandleLogout={onHandleLogout}
          primaryTextColor={primaryTextColor}
          emptyNote={emptyNote}
          isAdmin={isAdmin}
          website={website}
          isNavBarVisible={isNavBarVisible}
          isBackToSiteVisible={isBackToSiteVisible}
          mobileCustomArtUrl={mobileCustomArtUrl}
        />
      )}
      {/* Standalone registry page surfaces the page's own title (e.g. "Registry") below the
          event-name banner, matching the website page header on /registrylinks. */}
      {showPageTitle && (
        <Box
          width="100%"
          textAlign="center"
          marginBottom={emptyNote ? 6 : 5}
          paddingTop={bannerContentTopPadding}
        >
          <GuestSiteTypographyOverride override={display3ToPageTitleOverride}>
            <TextV2 typographyVariant="display3">{registryPageTitle}</TextV2>
          </GuestSiteTypographyOverride>
        </Box>
      )}
      <CoupleWrapper
        margin={{ md: '0 auto' }}
        paddingTop={showPageTitle ? undefined : bannerContentTopPadding}
      >
        <NoteAndPhotoRow>
          <StyledTextBox fullWidth={!isAdmin && !photoExists}>
            <Box>
              {eventDate && !isNative && (
                <Box
                  width="100%"
                  marginBottom={emptyNote ? 6 : 5}
                >
                  <GuestSiteTypographyOverride override={dateTypographyOverride}>
                    <DateText typographyVariant={isMobile ? 'hed3' : 'hed5'}>{eventDate}</DateText>
                  </GuestSiteTypographyOverride>
                </Box>
              )}
              <Box>
                <PageNoteText
                  isCustomStylesEnabled={true}
                  eventHandle={website}
                  pageId={eventPageId}
                  noteText={pageNote}
                  onClickReadMore={onContinueReading}
                  showEdit={isAdmin}
                  onEditPageNote={onEditPageNote}
                />
              </Box>
            </Box>
          </StyledTextBox>
          {(isAdmin || photoExists) && (
            <EventPhoto
              photo={photo}
              firebaseId={firebaseId}
              eventPageId={eventPageId}
              migratedPageContainerId={migratedPageContainerId}
              showEdit={isAdmin && !isNative}
              onDeletePhoto={handleDeletePhoto}
              onUpdatePhoto={handleOnUpdatePhoto}
            />
          )}
        </NoteAndPhotoRow>
      </CoupleWrapper>
    </Box>
  );
};
